import dayjs, { Dayjs } from 'dayjs';

const TIME_PATTERN = /^(\d{1,2}):(\d{2})$/;

interface EventDateTimeRangeInput {
  eventStartDate?: Date | string | null;
  eventEndDate?: Date | string | null;
  eventStartTime?: string | null;
  eventEndTime?: string | null;
}

interface EventDateTimeRangeValidation {
  message: string;
  fields: string[];
}

function buildEventDate(dateValue?: Date | string | null): Dayjs | null {
  if (!dateValue) {
    return null;
  }

  const date = dayjs(dateValue);
  if (!date.isValid()) {
    return null;
  }

  return date.startOf('day');
}

function buildEventDateTime(
  dateValue?: Date | string | null,
  timeValue?: string | null,
): Dayjs | null {
  if (!dateValue || !timeValue) {
    return null;
  }

  const match = String(timeValue).match(TIME_PATTERN);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  const date = dayjs(dateValue);
  if (!date.isValid()) {
    return null;
  }

  return date.hour(hours).minute(minutes).second(0).millisecond(0);
}

export function getEventDateTimeRangeValidation({
  eventStartDate,
  eventEndDate,
  eventStartTime,
  eventEndTime,
}: EventDateTimeRangeInput): EventDateTimeRangeValidation | null {
  const startDate = buildEventDate(eventStartDate);
  const endDate = buildEventDate(eventEndDate);

  if (startDate && endDate && startDate.isAfter(endDate)) {
    return {
      message: 'Start date cannot be later than end date',
      fields: ['eventStartDate', 'eventEndDate'],
    };
  }

  if (!eventStartTime || !eventEndTime) {
    return null;
  }

  const startDateTime = buildEventDateTime(eventStartDate, eventStartTime);
  const endDateTime = buildEventDateTime(eventEndDate || eventStartDate, eventEndTime);

  if (!startDateTime || !endDateTime) {
    return null;
  }

  if (startDateTime.isAfter(endDateTime)) {
    if (startDate && endDate && !startDate.isSame(endDate)) {
      return {
        message: 'Start date and time cannot be later than end date and time',
        fields: ['eventStartDate', 'eventEndDate', 'eventStartTime', 'eventEndTime'],
      };
    }

    return {
      message: 'Start time cannot be later than end time',
      fields: ['eventStartTime', 'eventEndTime'],
    };
  }

  if (startDateTime.isSame(endDateTime)) {
    return {
      message: 'Start time and end time cannot be the same',
      fields: ['eventStartTime', 'eventEndTime'],
    };
  }

  return null;
}

export function getEventDateTimeRangeError(input: EventDateTimeRangeInput): string | null {
  return getEventDateTimeRangeValidation(input)?.message || null;
}
